import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ConvoSession, ConvoSituationType, ConvoSessionStatus } from '../../types/convo.js';

const STATUS_LABELS: Record<ConvoSessionStatus, string> = {
  prep: 'Vorbereitung',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

const TYPE_LABELS: Record<ConvoSituationType, string> = {
  negotiation: 'Verhandlung',
  meeting: 'Meeting',
  sales: 'Sales',
  conflict: 'Konflikt',
  relationship: 'Beziehung',
  persuasion: 'Ueberzeugung',
};

@customElement('cp-convo-session-list')
export class CpConvoSessionList extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) sessions: ConvoSession[] = [];

  @state() private _searchQuery = '';
  @state() private _statusFilter = '';
  @state() private _typeFilter = '';

  private get _filteredSessions(): ConvoSession[] {
    let result = this.sessions;

    if (this._searchQuery.trim()) {
      const q = this._searchQuery.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(q) ||
        (s.description?.toLowerCase().includes(q) ?? false)
      );
    }

    if (this._statusFilter) {
      result = result.filter(s => s.status === this._statusFilter);
    }

    if (this._typeFilter) {
      result = result.filter(s => s.situation_type === this._typeFilter);
    }

    return result;
  }

  private _handleSessionClick(sessionId: number): void {
    this.dispatchEvent(new CustomEvent('session-selected', {
      detail: { sessionId },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleClearFilters(): void {
    this._searchQuery = '';
    this._statusFilter = '';
    this._typeFilter = '';
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  render() {
    const filtered = this._filteredSessions;
    const hasFilter = this._searchQuery.trim() || this._statusFilter || this._typeFilter;

    return html`
      <div class="cp-convo-session-list">
        <div class="cp-convo-session-list__filters">
          <div class="cp-convo-session-list__search-wrapper">
            <input
              class="cp-convo-session-list__search"
              type="text"
              placeholder="Session suchen..."
              .value=${this._searchQuery}
              @input=${(e: Event) => { this._searchQuery = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <select
            class="cp-convo-session-list__filter"
            .value=${this._statusFilter}
            @change=${(e: Event) => { this._statusFilter = (e.target as HTMLSelectElement).value; }}
          >
            <option value="">Alle Status</option>
            ${Object.entries(STATUS_LABELS).map(([val, label]) => html`
              <option value=${val} ?selected=${this._statusFilter === val}>${label}</option>
            `)}
          </select>
          <select
            class="cp-convo-session-list__filter"
            .value=${this._typeFilter}
            @change=${(e: Event) => { this._typeFilter = (e.target as HTMLSelectElement).value; }}
          >
            <option value="">Alle Typen</option>
            ${Object.entries(TYPE_LABELS).map(([val, label]) => html`
              <option value=${val} ?selected=${this._typeFilter === val}>${label}</option>
            `)}
          </select>
        </div>

        ${this._renderList(filtered, hasFilter)}
      </div>
    `;
  }

  private _renderList(filtered: ConvoSession[], hasFilter: boolean | string) {
    if (this.sessions.length === 0) {
      return html`
        <div class="cp-convo-session-list__empty">
          <div class="cp-convo-session-list__empty-icon">&#128172;</div>
          <p class="cp-convo-session-list__empty-title">Noch keine Sessions angelegt</p>
          <p class="cp-convo-session-list__empty-sub">Erstelle eine neue Session um ein Gespraech vorzubereiten</p>
        </div>
      `;
    }

    if (filtered.length === 0) {
      return html`
        <div class="cp-convo-session-list__empty">
          <div class="cp-convo-session-list__empty-icon">&#128269;</div>
          <p class="cp-convo-session-list__empty-title">Keine Sessions gefunden</p>
          ${hasFilter ? html`
            <button class="cp-convo-session-list__clear-btn" @click=${this._handleClearFilters}>
              Filter zuruecksetzen
            </button>
          ` : nothing}
        </div>
      `;
    }

    return html`
      <div class="cp-convo-session-list__grid">
        ${filtered.map(session => this._renderCard(session))}
      </div>
    `;
  }

  private _renderCard(session: ConvoSession) {
    return html`
      <div
        class="cp-convo-card"
        @click=${() => this._handleSessionClick(session.id)}
        role="button"
        tabindex="0"
        @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._handleSessionClick(session.id)}
      >
        <div class="cp-convo-card__header">
          <span class="cp-convo-card__type-badge cp-convo-card__type-badge--${session.situation_type}">
            ${TYPE_LABELS[session.situation_type]}
          </span>
          <span class="cp-convo-card__status cp-convo-card__status--${session.status}">
            ${STATUS_LABELS[session.status]}
          </span>
        </div>
        <div class="cp-convo-card__title">${session.title}</div>
        ${session.description ? html`
          <div class="cp-convo-card__desc">${session.description}</div>
        ` : nothing}
        <div class="cp-convo-card__footer">
          ${session.primary_goal ? html`
            <div class="cp-convo-card__goal">${session.primary_goal}</div>
          ` : nothing}
          <div class="cp-convo-card__date">${this._formatDate(session.updated_at)}</div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-convo-session-list': CpConvoSessionList;
  }
}
